import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { redeemOrgInviteToken } from "@services/api";
import { loadCurrentUserOrgs } from "@store/slices/orgsSlice";
import { useNotification } from "@shared/contexts/NotificationContext";

// Join landing page for org invite links (Phase 5).
//
// Flow:
//   1. Signed in → redeem immediately, navigate to /orgs/:slug/settings.
//   2. Signed out → store the join URL in sessionStorage and bounce to
//      the marketing landing; the AuthenticationModal honors the
//      stored redirect after sign-in/sign-up brings the user back here.
//
// Tokens are validated server-side (expired/revoked/missing all
// return distinct status codes); we surface whatever the server says.
const OrgJoinScreen = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { userId } = useAppSelector((state) => state.user);
  const { showSuccess, showError } = useNotification();
  const [status, setStatus] = useState<"idle" | "redeeming" | "error">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) return;

    // Not signed in: record where to come back to, then send the user
    // to the landing page where they can sign in / sign up.
    if (!userId) {
      sessionStorage.setItem("postAuthRedirect", `/orgs/join/${token}`);
      navigate("/");
      return;
    }

    // Signed in: redeem once. The status guard prevents the strict-mode
    // double-invoke of the effect from double-redeeming (which is also
    // idempotent server-side, but no point in racing).
    if (status !== "idle") return;
    setStatus("redeeming");

    (async () => {
      const result = await redeemOrgInviteToken(token);
      if (!result.success) {
        setStatus("error");
        setErrorMessage(result.error.message);
        showError(result.error.message);
        return;
      }
      showSuccess(`Joined ${result.data.name}`);
      dispatch(loadCurrentUserOrgs());
      navigate(`/orgs/${result.data.slug}/settings`);
    })();
  }, [token, userId, status, navigate, dispatch, showError, showSuccess]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
        {status === "error" ? (
          <>
            <Typography variant="h5" gutterBottom>
              Couldn't join the organization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {errorMessage || "The invite link is no longer valid."}
            </Typography>
            <Button variant="outlined" onClick={() => navigate("/annogram")}>
              Continue to ArgFinder
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h5" gutterBottom>
              Joining organization…
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ fontFamily: "monospace" }}>
                {token}
              </Box>
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default OrgJoinScreen;
